import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useRef, useEffect } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { Spacer, FieldSet, Flex, AdornedInput, Input, Adornment, FormHelp, Label, Popper, Popover, Paragraph, Anchor, Heading } from "@zillow/constellation";
import { FieldConfig, roundToNearestInteger, isSameFloat, roundToTheNearestTenth, validateAndTrackOptionsBlock, DISCOUNT_POINTS } from "./ClosingCostCalculator.utils.esm.js";
import { commaSeparateNumber, getCommaSeparatedNumber } from "../../utils/zhl-calculator.esm.js";
import useValidFieldUpdate from "../../utils/form/useValidFieldUpdate.esm.js";
const DownPaymentField = () => {
  var _a, _b;
  const form = useFormContext();
  const proxyFormState = useFormState();
  const error = ((_a = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.downPayment.name]) == null ? void 0 : _a.message) || ((_b = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.downPaymentPercent.name]) == null ? void 0 : _b.message);
  const fieldAmount = form.register(FieldConfig.downPayment.name, FieldConfig.downPayment.config);
  const fieldPercent = form.register(FieldConfig.downPaymentPercent.name, FieldConfig.downPaymentPercent.config);
  const amountInputRef = useRef(null);
  const formatAmount = commaSeparateNumber;
  useEffect(() => {
    if (formatAmount) {
      const defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  const trigger = /* @__PURE__ */ jsx(Popover, { header: /* @__PURE__ */ jsx(Heading, { level: 6, children: "Down payment" }), body: /* @__PURE__ */ jsxs(Paragraph, { children: [
    "A",
    " ",
    /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/learn/down-payment-on-a-house/", target: "_blank", children: "down payment" }),
    " ",
    "is the cash payment you make at closing toward the purchase of a home. Most loans require a down payment of at least 3% of the home purchase price. While not required, putting 20% down may help you avoid private mortgage insurance (PMI).",
    " ",
    /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/learn/private-mortgage-insurance/", target: "_blank", children: "Learn more" })
  ] }) });
  useSyncDownPaymentPercent();
  return /* @__PURE__ */ jsx(Spacer, { marginBottom: "xs", children: /* @__PURE__ */ jsx(FieldSet, { legend: /* @__PURE__ */ jsxs(Flex, { display: "flex", alignItems: "center", children: [
    /* @__PURE__ */ jsx(Label, { htmlFor: "downPaymentAmount", children: "Down payment" }),
    /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
      " ",
      /* @__PURE__ */ jsx(Popper, { triggered: trigger })
    ] })
  ] }), description: (
    // Use blank space when no error or description to avoid
    // layout shift when when error is shown.
    /* @__PURE__ */ jsx(FormHelp, { error: Boolean(error), children: error || /* @__PURE__ */ jsx(React__default.Fragment, { children: " " }) })
  ), required: true, children: /* @__PURE__ */ jsxs(Flex, { display: "flex", children: [
    /* @__PURE__ */ jsx(AdornedInput, { style: {
      flex: "1"
    }, error: Boolean(error), leftAdornment: /* @__PURE__ */ jsx(Adornment, { children: "$" }), input: /* @__PURE__ */ jsx(Input, { style: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderRightWidth: 0
    }, id: "downPaymentAmount", ...fieldAmount, ref: (el) => {
      amountInputRef.current = el;
      fieldAmount.ref(el);
    }, onInput: formatAmount ? (e) => {
      e.target.value = formatAmount(e.target.value);
    } : void 0, autoComplete: "off" }) }),
    /* @__PURE__ */ jsx(AdornedInput, { style: {
      flex: "0.6"
    }, error: Boolean(error), rightAdornment: /* @__PURE__ */ jsx(Adornment, { children: "%" }), input: /* @__PURE__ */ jsx(Input, { ...fieldPercent, autoComplete: "off", style: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    } }) })
  ] }) }) });
};
function useSyncDownPaymentPercent() {
  const form = useFormContext();
  const lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  const updateDiscountPoint = () => {
    if (lastValidFields.current[FieldConfig.purchasingAnyDiscountPoints.name] == DISCOUNT_POINTS.oneDiscountPoint) {
      const nextPurchasingAnyDiscountPoints = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.01;
      form.setValue(FieldConfig.discountPoints.name, getCommaSeparatedNumber(roundToNearestInteger(nextPurchasingAnyDiscountPoints)), validateAndTrackOptionsBlock);
    } else if (lastValidFields.current[FieldConfig.purchasingAnyDiscountPoints.name] == DISCOUNT_POINTS.twoDiscountPoints) {
      const nextPurchasingAnyDiscountPoints = 2 * (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.01;
      form.setValue(FieldConfig.discountPoints.name, getCommaSeparatedNumber(roundToNearestInteger(nextPurchasingAnyDiscountPoints)), validateAndTrackOptionsBlock);
    } else {
      const nextPurchasingAnyDiscountPoints = 0;
      form.setValue(FieldConfig.discountPoints.name, nextPurchasingAnyDiscountPoints, validateAndTrackOptionsBlock);
    }
  };
  useValidFieldUpdate(form, (name, value) => {
    lastValidFields.current = {
      ...lastValidFields.current,
      [name]: value
    };
    if (name === FieldConfig.downPayment.name) {
      const nextDownPaymenPercent = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      const nextLoanOriginationFee = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.5 / 100;
      const nextPrepaidInterest = roundToNearestInteger((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      if (!isSameFloat(nextDownPaymenPercent, lastValidFields.current.downPaymentPercent)) {
        form.setValue(FieldConfig.downPaymentPercent.name, roundToTheNearestTenth(nextDownPaymenPercent), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextLoanOriginationFee, lastValidFields.current.loanOriginationFee)) {
        form.setValue(FieldConfig.loanOriginationFee.name, getCommaSeparatedNumber(roundToNearestInteger(nextLoanOriginationFee)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextPrepaidInterest, lastValidFields.current.prepaidInterest)) {
        form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(roundToNearestInteger(nextPrepaidInterest)), validateAndTrackOptionsBlock);
      }
      updateDiscountPoint();
    }
    if (name === FieldConfig.homePrice.name) {
      const nextDownPaymentPercent = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      const nextLoanOriginationFee = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.5 / 100;
      const nextTitleInsurance = lastValidFields.current[FieldConfig.homePrice.name] * 0.5 / 100;
      const nextSettelementOrEscrowFee = lastValidFields.current[FieldConfig.homePrice.name] * 0.25 / 100;
      const nextHomeownerInsurance = lastValidFields.current[FieldConfig.homePrice.name] * 0.35 / 100;
      const nextPrepaidInterest = roundToNearestInteger((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      const nextPropertyTaxesAmount = lastValidFields.current[FieldConfig.propertyTaxRate.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name] / 4;
      form.setValue(FieldConfig.downPaymentPercent.name, roundToTheNearestTenth(nextDownPaymentPercent), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(roundToNearestInteger(nextPrepaidInterest)), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.propertyTaxes.name, getCommaSeparatedNumber(roundToNearestInteger(nextPropertyTaxesAmount)), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.loanOriginationFee.name, getCommaSeparatedNumber(roundToNearestInteger(nextLoanOriginationFee)), validateAndTrackOptionsBlock);
      if (!isSameFloat(nextTitleInsurance, !lastValidFields.current.titleInsurance ? 0 : lastValidFields.current.titleInsurance)) {
        form.setValue(FieldConfig.titleInsurance.name, getCommaSeparatedNumber(roundToNearestInteger(nextTitleInsurance)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextSettelementOrEscrowFee, !lastValidFields.current.settelementOrEscrowFee ? 0 : lastValidFields.current.settelementOrEscrowFee)) {
        form.setValue(FieldConfig.settelementOrEscrowFee.name, getCommaSeparatedNumber(roundToNearestInteger(nextSettelementOrEscrowFee)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextHomeownerInsurance, !lastValidFields.current.homeownerInsurance ? 0 : lastValidFields.current.homeownerInsurance)) {
        form.setValue(FieldConfig.homeownerInsurance.name, getCommaSeparatedNumber(roundToNearestInteger(nextHomeownerInsurance)), validateAndTrackOptionsBlock);
      }
      updateDiscountPoint();
    }
    if (name === FieldConfig.purchasingAnyDiscountPoints.name) {
      updateDiscountPoint();
    }
    if (name === FieldConfig.interestRate.name) {
      const nextPrepaidInterest = Math.round((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(nextPrepaidInterest), validateAndTrackOptionsBlock);
    }
  });
}
export {
  DownPaymentField as default
};
